import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Trophy,
  Target,
  Users,
  Flame,
  Star,
  ChevronLeft,
  Lock,
  CheckCircle2,
  Coins,
  TrendingUp
} from "lucide-react";
import { Button } from "./ui/button";
import { Progress } from "./ui/progress";

const API = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : '/api';

// Category config with static Tailwind classes (dynamic interpolation doesn't work with JIT)
const CATEGORY_CONFIG = {
  track: { 
    icon: Target, 
    label: "Track",
    bgClass: "bg-emerald-500/20",
    borderClass: "border-emerald-500/30",
    textClass: "text-emerald-400"
  },
  connect: { 
    icon: Users, 
    label: "Connect",
    bgClass: "bg-blue-500/20",
    borderClass: "border-blue-500/30",
    textClass: "text-blue-400"
  },
  compete: { 
    icon: Trophy, 
    label: "Compete",
    bgClass: "bg-amber-500/20",
    borderClass: "border-amber-500/30",
    textClass: "text-amber-400"
  },
  engagement: { 
    icon: Flame, 
    label: "Engagement",
    bgClass: "bg-orange-500/20",
    borderClass: "border-orange-500/30",
    textClass: "text-orange-400"
  },
  seasonal: { 
    icon: Star, 
    label: "Seasonal",
    bgClass: "bg-pink-500/20",
    borderClass: "border-pink-500/30",
    textClass: "text-pink-400"
  }
};

const BadgeCard = ({ badge, onNewUnlock }) => {
  const [showReward, setShowReward] = useState(false);
  const categoryConfig = CATEGORY_CONFIG[badge.category] || CATEGORY_CONFIG.track;
  const CategoryIcon = categoryConfig.icon;

  useEffect(() => {
    if (badge.just_unlocked) {
      setShowReward(true);
      toast.success(`Badge unlocked: ${badge.name}! +${badge.coins_reward} coins`);
      setTimeout(() => setShowReward(false), 3000);
    }
  }, [badge]);

  return (
    <div 
      data-testid={`badge-card-${badge.badge_id}`}
      data-unlocked={badge.unlocked ? "true" : "false"}
      className={`relative p-4 rounded-xl border transition-all ${
        badge.unlocked 
          ? 'bg-gray-800/50 border-gray-700' 
          : 'bg-gray-900/30 border-gray-800/50 opacity-70'
      }`}
    >
      {/* Just unlocked glow */}
      {badge.just_unlocked && (
        <div className="absolute inset-0 rounded-xl bg-gradient-to-r from-amber-500/20 to-emerald-500/20 animate-pulse" />
      )}

      <div className="relative flex items-start gap-4">
        {/* Badge Icon */}
        <div className={`w-14 h-14 rounded-xl flex items-center justify-center text-2xl ${
          badge.unlocked 
            ? `${categoryConfig.bgClass} border ${categoryConfig.borderClass}` 
            : 'bg-gray-800 border border-gray-700'
        }`}>
          {badge.unlocked ? badge.icon : <Lock className="w-6 h-6 text-gray-600" />}
        </div>

        {/* Badge Info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <h3 className={`font-semibold ${badge.unlocked ? 'text-white' : 'text-gray-500'}`}>
              {badge.name}
            </h3>
            {badge.unlocked && (
              <CheckCircle2 className="w-4 h-4 text-emerald-400" />
            )}
          </div>
          <p className={`text-sm ${badge.unlocked ? 'text-gray-400' : 'text-gray-600'}`}>
            {badge.description}
          </p>

          {/* Progress Bar */}
          {!badge.unlocked && (
            <div className="mt-3">
              <div className="flex justify-between text-xs mb-1">
                <span className="text-gray-500">Progress</span>
                <span className="text-gray-400">{badge.current}/{badge.target}</span>
              </div>
              <Progress data-testid={`badge-progress-${badge.badge_id}`} value={badge.progress} className="h-2" />
            </div>
          )}

          {/* Coin Reward */}
          {badge.coins_reward > 0 && (
            <div className={`flex items-center gap-1 mt-2 ${
              badge.unlocked ? 'text-amber-400/70' : 'text-gray-600'
            }`}>
              <Coins className="w-3 h-3" />
              <span className="text-xs">
                {badge.unlocked ? 'Earned' : 'Reward'}: {badge.coins_reward} coins
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Category tag */}
      <div className={`absolute top-2 right-2 px-2 py-0.5 rounded-full text-xs ${
        badge.unlocked 
          ? `${categoryConfig.bgClass} ${categoryConfig.textClass}` 
          : 'bg-gray-800 text-gray-600'
      }`}>
        {categoryConfig.label}
      </div>
    </div>
  );
};

const AchievementsPage = ({ user }) => {
  const navigate = useNavigate();
  const [badges, setBadges] = useState([]);
  const [seasonalBadges, setSeasonalBadges] = useState([]);
  const [stats, setStats] = useState(null);
  const [currentSeason, setCurrentSeason] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all, unlocked, locked

  const fetchAchievements = useCallback(async () => {
    try {
      setLoading(true);
      const [achievementsRes, seasonalRes] = await Promise.all([
        axios.get(`${API}/achievements`),
        axios.get(`${API}/achievements/seasonal`)
      ]);
      
      setBadges(achievementsRes.data.badges || []);
      setStats(achievementsRes.data.stats);
      setSeasonalBadges(seasonalRes.data.seasonal_badges || []);
      setCurrentSeason(seasonalRes.data.current_season);
      
      // Show toast for newly unlocked badges
      if (achievementsRes.data.newly_unlocked?.length > 0) {
        achievementsRes.data.newly_unlocked.forEach(badge => {
          toast.success(`🎉 Badge unlocked: ${badge.name}! +${badge.coins_reward} coins`);
        });
      }
    } catch (error) {
      console.error("Error fetching achievements:", error);
      toast.error("Failed to load achievements");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAchievements();
  }, [fetchAchievements]);

  const filteredBadges = badges.filter(badge => {
    if (filter === 'unlocked') return badge.unlocked;
    if (filter === 'locked') return !badge.unlocked;
    return true;
  });

  const unlockedCount = badges.filter(b => b.unlocked).length;

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="w-8 h-8 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="ps-theme-page min-h-screen pb-24" data-testid="achievements-page">
      {/* Header */}
      <div className="ps-page-header pt-4 pb-6 px-4" data-testid="achievements-header">
        <div className="max-w-lg mx-auto">
          {/* Back Button */}
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-gray-400 hover:text-white mb-4"
          >
            <ChevronLeft className="w-5 h-5" />
            <span>Back</span>
          </button>

          {/* Title & Progress */}
          <div className="text-center mb-6">
            <div className="text-5xl mb-3">🏆</div>
            <h1 data-testid="achievements-title" className="text-2xl font-bold text-white mb-2">Achievements</h1>
            <p data-testid="achievements-unlocked-count" className="text-gray-400">
              {unlockedCount} of {badges.length} badges unlocked
            </p>
            <Progress 
              value={(unlockedCount / badges.length) * 100} 
              className="h-2 mt-3 max-w-xs mx-auto" 
            />
          </div>

          {/* Stats Summary */}
          {stats && (
            <div className="grid grid-cols-3 gap-3 mb-4">
              <div className="bg-gray-800/50 rounded-xl p-3 text-center">
                <div className="text-2xl font-bold text-white">{stats.rounds}</div>
                <div className="text-xs text-gray-400">Rounds</div>
              </div>
              <div className="bg-gray-800/50 rounded-xl p-3 text-center">
                <div className="text-2xl font-bold text-white">{stats.streak}</div>
                <div className="text-xs text-gray-400">Day Streak</div>
              </div>
              <div className="bg-gray-800/50 rounded-xl p-3 text-center">
                <div className="text-2xl font-bold text-white">{stats.buddies}</div>
                <div className="text-xs text-gray-400">Buddies</div>
              </div>
            </div>
          )}

          {/* Filter Tabs */}
          <div className="flex gap-2">
            {['all', 'unlocked', 'locked'].map((f) => (
              <button
                key={f}
                data-testid={`achievements-filter-${f}`}
                onClick={() => setFilter(f)}
                className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-colors ${
                  filter === f
                    ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30'
                    : 'bg-gray-800/50 text-gray-400 border border-gray-800 hover:bg-gray-800'
                }`}
              >
                {f === 'all' ? 'All' : f === 'unlocked' ? 'Unlocked' : 'Locked'}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Seasonal Badges */}
      {seasonalBadges.length > 0 && (
        <div className="px-4 py-4" data-testid="seasonal-badges-section">
          <div className="max-w-lg mx-auto">
            <div className="flex items-center gap-2 mb-3">
              <span className="text-xl">
                {currentSeason === 'winter' && '❄️'}
                {currentSeason === 'spring' && '🌸'}
                {currentSeason === 'summer' && '☀️'}
                {currentSeason === 'fall' && '🍂'}
              </span>
              <h2 data-testid="seasonal-badges-heading" className="text-lg font-semibold text-white capitalize">{currentSeason} Badges</h2>
              <span data-testid="seasonal-limited-time-tag" className="text-xs px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-400">LIMITED TIME</span>
            </div>
            <div className="space-y-3">
              {seasonalBadges.map((badge) => (
                <BadgeCard key={badge.badge_id} badge={{...badge, category: 'seasonal'}} />
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Badges Grid */}
      <div className="px-4 py-4">
        <div className="max-w-lg mx-auto">
          <h2 className="text-lg font-semibold text-white mb-3">All Badges</h2>
          <div className="space-y-3">
            {filteredBadges.length === 0 ? (
              <div className="text-center py-12">
                <Star className="w-12 h-12 text-gray-700 mx-auto mb-3" />
                <p className="text-gray-500">No badges in this category</p>
              </div>
            ) : (
              filteredBadges.map((badge) => (
                <BadgeCard key={badge.badge_id} badge={badge} />
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AchievementsPage;
